import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
import { WINDOW_BROKER_PROXY_LOGGER_NAME, getAllowedOrigins } from '../../config';
import { RelayError } from '../../errors';
import { Logger } from 'simple-logging-lib';
import { delay, getDeferred } from '../../utils';
import { relayOperations } from './operations';
import { checkIfHasBrokerProxy } from './proxy-probe';
var _instance = /*#__PURE__*/_classPrivateFieldLooseKey("instance");
var _initialized = /*#__PURE__*/_classPrivateFieldLooseKey("initialized");
var _targetOrigin = /*#__PURE__*/_classPrivateFieldLooseKey("targetOrigin");
var _brokerId = /*#__PURE__*/_classPrivateFieldLooseKey("brokerId");
export class WindowBrokerProxy {
  constructor(scope) {
    this.logger = Logger.getLogger(WINDOW_BROKER_PROXY_LOGGER_NAME);
    this.link = null;
    this.handshakeCompleted = false;
    Object.defineProperty(this, _initialized, {
      writable: true,
      value: false
    });
    Object.defineProperty(this, _targetOrigin, {
      writable: true,
      value: null
    });
    Object.defineProperty(this, _brokerId, {
      writable: true,
      value: null
    });
    this.waitForCompletion = requestId => {
      const deferred = getDeferred();
      const onReply = event => {
        if (relayOperations.opCompletedConfirmation.match(event.data) && event.data.payload === requestId) {
          deferred.resolve();
          this.relayPort.removeEventListener('message', onReply);
        }
      };
      this.relayPort.addEventListener('message', onReply);
      return deferred.promise;
    };
    this.waitForError = requestId => {
      const deferred = getDeferred();
      const onError = event => {
        if (relayOperations.opFailedConfirmation.match(event.data) && event.data.payload.replyTo === requestId) {
          deferred.reject(event.data.payload.error);
          this.relayPort.removeEventListener('message', onError);
        }
      };
      this.relayPort.addEventListener('message', onError);
      return deferred.promise;
    };
    this.waitForOperationResult = requestId => {
      const timeout = delay(5000);
      const onSuccess = () => {
        timeout.reset();
      };
      return Promise.race([this.waitForCompletion(requestId).then(onSuccess), this.waitForError(requestId).then(onSuccess), timeout.then(() => {
        throw new RelayError(RelayError.Reason.OPERATION_TIMEOUT);
      })]);
    };
    this.init = async () => {
      const result = await checkIfHasBrokerProxy(this.scope);
      if (!(result !== null && result !== void 0 && result.origin)) {
        throw new RelayError(RelayError.Reason.ORIGIN_NOT_RETRIEVED);
      }
      if (!getAllowedOrigins().includes(result.origin)) {
        throw new RelayError(RelayError.Reason.ORIGIN_NOT_ALLOWED);
      }
      _classPrivateFieldLooseBase(this, _targetOrigin)[_targetOrigin] = result.origin;
      _classPrivateFieldLooseBase(this, _brokerId)[_brokerId] = result.brokerId;
      _classPrivateFieldLooseBase(this, _initialized)[_initialized] = true;
    };
    this.handshake = (payload, link) => {
      if (!_classPrivateFieldLooseBase(this, _initialized)[_initialized]) {
        throw new RelayError(RelayError.Reason.PROXY_NOT_INITIALIZED);
      }
      if (!_classPrivateFieldLooseBase(this, _targetOrigin)[_targetOrigin]) {
        throw new RelayError(RelayError.Reason.ORIGIN_NOT_RETRIEVED);
      }
      const message = relayOperations.handshake(payload);
      const deferred = getDeferred();
      this.link = link;
      const onReply = event => {
        if (!relayOperations.handshakeResponse.match(event.data) || event.data.payload.replyTo !== message.id) {
          return;
        }
        if (event.origin !== _classPrivateFieldLooseBase(this, _targetOrigin)[_targetOrigin]) {
          this.logger.debug('Ignoring message from invalid origin', event.origin);
        }
        this.logger.debug('Proxy handshake completed', event.data.payload);
        deferred.resolve(event.data.payload);
        this.relayPort.removeEventListener('message', onReply);
        this.handshakeCompleted = true;
        _classPrivateFieldLooseBase(this, _brokerId)[_brokerId] = event.data.payload.brokerId;
      };
      this.relayPort.addEventListener('message', onReply);
      this.logger.debug('Sending proxy handshake message', message);
      const transfer = [this.messageChannel.port2];
      this.scope.postMessage(message, _classPrivateFieldLooseBase(this, _targetOrigin)[_targetOrigin], transfer);
      this.relayPort.addEventListener('message', this.onRelayMessage);
      return deferred.promise;
    };
    this.onRelayMessage = event => {
      if (!relayOperations.relayMessage.match(event.data)) {
        return;
      }
      if (event.origin !== _classPrivateFieldLooseBase(this, _targetOrigin)[_targetOrigin]) {
        this.logger.debug('Ignoring relay message from invalid origin', event.origin);
      }
      if (!this.link) {
        this.logger.error(new RelayError(RelayError.Reason.NO_LINK_SET));
        return;
      }
      this.link.next(event.data.payload);
    };
    this.topicSubscribe = payload => {
      if (!this.handshakeCompleted) {
        throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
      }
      const message = relayOperations.topicSubscribe(payload);
      const completionPromise = this.waitForOperationResult(message.id);
      this.relayPort.postMessage(message);
      return completionPromise;
    };
    this.topicUnsubscribe = payload => {
      if (!this.handshakeCompleted) {
        throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
      }
      const message = relayOperations.topicUnsubscribe(payload);
      const completionPromise = this.waitForOperationResult(message.id);
      this.relayPort.postMessage(message);
      return completionPromise;
    };
    this.getTopicSubscriptions = payload => {
      if (!this.handshakeCompleted) {
        throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
      }
      const deferred = getDeferred();
      const message = relayOperations.getTopicSubscriptions(payload);
      const onReply = event => {
        if (relayOperations.getTopicSubscriptionsResponse.match(event.data) && event.data.payload.replyTo === message.id) {
          deferred.resolve(event.data.payload);
        }
      };
      this.relayPort.addEventListener('message', onReply);
      this.relayPort.postMessage(message);
      return deferred.promise;
    };
    this.close = clientId => {
      if (!this.handshakeCompleted) {
        throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
      }
      const message = relayOperations.close({
        clientId
      });
      const completionPromise = this.waitForOperationResult(message.id);
      this.relayPort.postMessage(message);
      return completionPromise;
    };
    this.reset = () => {
      var _this$link;
      (_this$link = this.link) === null || _this$link === void 0 || _this$link.complete();
      this.relayPort.close();
      this.handshakeCompleted = false;
      this.messageChannel.port2.close();
      // @ts-ignore - Resetting the singleton instance for testing purposes
      _classPrivateFieldLooseBase(WindowBrokerProxy, _instance)[_instance] = undefined;
      return Promise.resolve();
    };
    this.sendMessage = payload => {
      if (!this.handshakeCompleted) {
        throw new RelayError(RelayError.Reason.HANDSHAKE_NOT_COMPLETED);
      }
      const message = relayOperations.sendMessage(payload);
      const completionPromise = this.waitForOperationResult(message.id);
      this.relayPort.postMessage(message);
      return completionPromise;
    };
    this.getBrokerId = () => {
      if (!_classPrivateFieldLooseBase(this, _initialized)[_initialized]) {
        throw new RelayError(RelayError.Reason.PROXY_NOT_INITIALIZED);
      }
      if (!_classPrivateFieldLooseBase(this, _brokerId)[_brokerId]) {
        throw new RelayError(RelayError.Reason.PROXY_NOT_INITIALIZED);
      }
      return _classPrivateFieldLooseBase(this, _brokerId)[_brokerId];
    };
    this.scope = scope;
    // Send message channel port to the scope
    const messageChannel = new MessageChannel();
    this.messageChannel = messageChannel;
    messageChannel.port1.start();
    this.relayPort = messageChannel.port1;
  }
}
Object.defineProperty(WindowBrokerProxy, _instance, {
  writable: true,
  value: void 0
});
WindowBrokerProxy.create = async scope => {
  if (!_classPrivateFieldLooseBase(WindowBrokerProxy, _instance)[_instance]) {
    const instance = new WindowBrokerProxy(scope);
    try {
      await instance.init();
    } catch (error) {
      instance.logger.error(error);
      throw error;
    }
    _classPrivateFieldLooseBase(WindowBrokerProxy, _instance)[_instance] = instance;
  }
  return _classPrivateFieldLooseBase(WindowBrokerProxy, _instance)[_instance];
};
WindowBrokerProxy.reset = async () => {
  if (!_classPrivateFieldLooseBase(WindowBrokerProxy, _instance)[_instance]) {
    return;
  }
  await _classPrivateFieldLooseBase(WindowBrokerProxy, _instance)[_instance].reset();
  _classPrivateFieldLooseBase(WindowBrokerProxy, _instance)[_instance] = undefined;
};